'use client';

import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import React from 'react';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';

import Modal from '@cineamo/ui/src/modals/Modal';

const gretaInfoImage = loadAsset('/images/im-gretaInfoV2.png');

/** GRETA app landing pages — anything other than German gets the multilingual page. */
const GRETA_APP_URLS: Record<string, string> = {
    de: 'https://www.gretaundstarks.de/greta/greta',
    en: 'https://gretaundstarks.de/multilingual/'
};

export type GretaInfoModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

/**
 * GRETA accessibility info popup: what the app does, and a link to it. Built on the shared Modal,
 * which takes its panel and text colours from the `--non-transparent-surface-color` variables — so
 * this renders in the Cineamo theme on the web app and in the cinema's own theme on a cinema
 * website, without either of them passing a configuration in.
 */
export default function GretaInfoModal(props: GretaInfoModalProps): React.JSX.Element {
    const t = useTranslations('greta');
    const locale = useLocale();

    // locale may carry a region (`de-DE`) — the URL map is keyed by language alone
    const link = GRETA_APP_URLS[locale.split(/[-_]/)[0]] ?? GRETA_APP_URLS.en;

    return (
        <Modal
            modalIsOpen={props.isOpen}
            // The open state lives in the caller, so the Modal's own setter only has to relay a
            // close back to it (it is the one value the Modal ever sets).
            setModalIsOpen={(value) => {
                if (value === false) {
                    props.onClose();
                }
            }}
            onClose={props.onClose}
            title={t('title')}>
            <div className="flex flex-col space-y-16">
                <Image
                    className="w-full h-auto rounded-8 object-cover max-h-[378px] max-w-[534px] mx-auto"
                    src={gretaInfoImage}
                    alt="GRETA"
                    width={534}
                    height={378}
                />

                <p className="text-body-14 px-8">
                    {t.rich('description', {
                        bold: (chunks) => <strong>{chunks}</strong>
                    })}
                </p>

                <a
                    className="flex items-center justify-center w-full py-14 rounded-4 text-body-bold-14 text-[var(--on-primary-color)] bg-[var(--primary-color)]"
                    href={link}
                    target="_blank"
                    rel="noreferrer">
                    {t('cta')}
                </a>
            </div>
        </Modal>
    );
}
